import { isProductionEnvironment } from 'bootstrapper/utils';
import type { AppEnvironment } from 'contexts/environment/environment.context';
import { useReportErrorCallback } from 'contexts/errorReportingContext';
import type { UserType } from 'contexts/user.context';
import { useAppSelector } from 'hooks/useAppSelector';
import { useIsInternalUser } from 'hooks/useIsInternalUser';
import { FC, PropsWithChildren, ReactNode } from 'react';
import { useDispatch } from 'react-redux';
import { getToggleFeatures, getUserPreferencesTheme, getUserPreferencesUnderlyingCodeType } from 'state/selectors';
import { isApplicationReady } from 'state/user';

import { AppRoot } from './AppRoot';
import { AppWrapper } from './AppWrapper';

export const ConnectedAppWrapper: FC<{
  children: ReactNode;
  environment: string;
}> = ({ environment, children }) => {
  const theme = useAppSelector(getUserPreferencesTheme);
  const isReady = useAppSelector(isApplicationReady);
  const defaultUnderlyingCodeType = useAppSelector(getUserPreferencesUnderlyingCodeType);
  const isInternalUser = useIsInternalUser();
  const userType: UserType = isInternalUser ? 'internalSale' : 'externalClient';
  const appEnv: AppEnvironment = isProductionEnvironment(environment) ? 'Production' : 'NonProduction';
  const features = useAppSelector(getToggleFeatures);
  const dispatch = useDispatch();
  const reportError = useReportErrorCallback();

  return (
    <AppWrapper
      theme={theme}
      isReady={isReady}
      defaultUnderlyingCodeType={defaultUnderlyingCodeType}
      userType={userType}
      environment={appEnv}
      features={features}
      dispatch={dispatch}
      reportError={reportError}
    >
      {children}
    </AppWrapper>
  );
};

ConnectedAppWrapper.displayName = 'ConnectedAppWrapper';

export const App: FC<{ environment: string; Router: FC<PropsWithChildren> }> = ({ environment, Router }) => (
  <ConnectedAppWrapper environment={environment}>
    <AppRoot Router={Router} />
  </ConnectedAppWrapper>
);

App.displayName = 'App';
